import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { AlertComponent } from '../components/alert/alert.component';
import { PromptComponent } from '../components/prompt/prompt.component';
import { LoadingComponent } from '../components/loading/loading.component';
import { ConfirmComponent } from '../components/confirm/confirm.component';
import { Router } from '@angular/router';
import { ConfirmConfig, ImagePickerConfig, PromptDialogDataType } from '../types';
import { PhoneNumberUtil, PhoneNumberFormat } from 'google-libphonenumber';
import { AlphabeticCountriesCodes } from '../consts/countries';
import { ImagePickerComponent } from '../components/image-picker/image-picker.component';
import moment from 'moment';
import { environment } from 'src/environments/environment';
import { firstValueFrom, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';

@Injectable()
export class ToolsService {
  public NS_URL = environment.NS_URL;
  public months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  constructor(
    public snackBar: MatSnackBar,
    public dialog: MatDialog,
    public router: Router,
    private httpClient: HttpClient
  ) {}

  public EURIC(url: string, queries: object): string {
    let str = '';
    for (const key in queries) {
      if (Object.prototype.hasOwnProperty.call(queries, key)) {
        str !== '' ? (str += '&') : null;
        str += `${key}=${encodeURIComponent(queries[key])}`;
      }
    }
    return `${url}?${str}`;
  }

  public setPhone(phoneNumber): string {
    phoneNumber = (phoneNumber || '').trim();
    return phoneNumber.length === 10 ? `1${phoneNumber}` : '';
  }

  public uploadPhoto = async (options: ImagePickerConfig): Promise<string> => {
    const dialogRef = this.dialog.open(ImagePickerComponent, {
      data: {
        format: 'png',
        size: 512,
        maintainAspectRatio: true,
        ...options,
      },
      panelClass: 'image-uploader-model',
    });
    return await dialogRef.afterClosed().toPromise();
  };

  public showPhotoSelectionDialog(options: ImagePickerConfig): Observable<Blob> {
    const dialogRef = this.dialog.open(ImagePickerComponent, {
      data: {
        format: 'png',
        size: 512,
        maintainAspectRatio: true,
        returnBlob: true,
        ...options,
      },
      panelClass: 'image-uploader-model',
      maxWidth: '50%',
    });
    return dialogRef.afterClosed();
  }

  public confirm = async (data: ConfirmConfig): Promise<boolean> =>
    await firstValueFrom(this.dialog.open(ConfirmComponent, { data }).afterClosed());

  public confirmObservable = (data: ConfirmConfig): Observable<boolean> =>
    this.dialog.open(ConfirmComponent, { data }).afterClosed();

  public prompt = async (data: PromptDialogDataType): Promise<string | null> =>
    await firstValueFrom(this.dialog.open(PromptComponent, { data }).afterClosed());

  public alert(title: string, message: string, button?: string): void {
    this.dialog.open(AlertComponent, { data: { title, message, button } });
  }

  public toast(message: string, action: string): void {
    this.snackBar.open(message, action, { duration: 2000 });
  }

  public loading(type = false, message?: string): void {
    const dialogRef = this.dialog.getDialogById('loading-dialog');

    if (type && !dialogRef) {
      this.dialog.open(LoadingComponent, { data: { message }, id: 'loading-dialog' });
    } else if (!type && dialogRef) {
      dialogRef.close();
    }
  }
  validateEmail(email) {
    const re =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  }

  comparefirstName(a, b) {
    if (a?.firstName?.toLowerCase() < b?.firstName?.toLowerCase()) {
      return -1;
    }
    if (a?.firstName?.toLowerCase() > b?.firstName?.toLowerCase()) {
      return 1;
    }
    return 0;
  }

  compareIndex(a, b) {
    if (a.index < b.index) {
      return -1;
    }
    if (a.index > b.index) {
      return 1;
    }
    return 0;
  }

  removeItem(arr, item) {
    return arr.filter((f) => f !== item);
  }

  public sortNumber(a, b, isAsc): number {
    a = Number(a || 0);
    b = Number(b || 0);
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  public compare(a: number | string, b: number | string, isAsc: boolean): number {
    a = a || '';
    b = b || '';
    if (typeof a === 'string') a = a.toLowerCase().trim();
    if (typeof b === 'string') b = b.toLowerCase().trim();

    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  public getPathStorageFromUrl(url: string): string {
    let baseUrl = url.split('/o/')[0];
    baseUrl = baseUrl + '/o/';
    let imagePath: string = url.replace(baseUrl, '');
    const indexOfEndPath = imagePath.indexOf('?');
    imagePath = imagePath.substring(0, indexOfEndPath);
    imagePath = imagePath.split('%2F').join('/');
    imagePath = imagePath.split('%20').join(' ');
    return imagePath;
  }

  private async invalidateToken(): Promise<string | void> {
    return firstValueFrom(this.httpClient.post(`${this.NS_URL}/auth/logout`, {}, { responseType: 'text' })).catch(
      () => {
        console.error('Error invalidating token');
      }
    );
  }

  public async doLogout(): Promise<void> {
    await this.invalidateToken();
    localStorage.clear();
    location.reload();
  }

  public changeRoute(queryParams: object, path?: string): void {
    this.router.navigate(path ? [path] : [], {
      queryParams,
      queryParamsHandling: 'merge',
    });
  }

  public getPhoneNumberInE164Format(num: string): string {
    if (num) {
      const phoneUtil = PhoneNumberUtil.getInstance();
      const PNF = PhoneNumberFormat;
      const number = phoneUtil.parseAndKeepRawInput(num, AlphabeticCountriesCodes.US);
      return phoneUtil.format(number, PNF.E164);
    }
    return num;
  }

  public getPhoneNumberInNationalFormat(num: string): string {
    if (num) {
      const phoneUtil = PhoneNumberUtil.getInstance();
      const PNF = PhoneNumberFormat;
      const number = phoneUtil.parseAndKeepRawInput(num, AlphabeticCountriesCodes.US);
      return phoneUtil.format(number, PNF.NATIONAL);
    }
    return num;
  }

  public isValidPhoneNumber(phoneNumber: string): boolean {
    if (!phoneNumber) return false;
    const phoneUtil = PhoneNumberUtil.getInstance();
    try {
      const parsedPhoneNumber = phoneUtil.parse(phoneNumber, AlphabeticCountriesCodes.US);
      return phoneUtil.isValidNumber(parsedPhoneNumber);
    } catch (error) {
      console.error('Error parsing phone number:', error);
      return false;
    }
  }

  addDateSuffix(name: string): string {
    const dateSuffix = moment().format('YYYY-MM-DD');
    return `${name}_${dateSuffix}`;
  }

  public goBack(): void {
    history.back();
  }
}
